// Sign-In — Phase B3A: Supabase phone-OTP authentication (primary).
//
// Flow states:
//   'phone'   — user enters E.164 mobile number, we call
//               supabase.auth.signInWithOtp({ phone })
//   'otp'     — user enters the 6-digit SMS code, we call
//               supabase.auth.verifyOtp({ phone, token, type: 'sms' })
//   'signed'  — verified; we display session summary + sign-out button
//
// Guarantees:
//   * No OTP is ever logged.
//   * No access_token / refresh_token is ever logged.
//   * Only the sanitized error message is surfaced to the user.
//   * We do not insert or fabricate an auth.users record.
//   * We do not create a customers row, draft, plan, or any other
//     application data in this phase (Phase B3A scope boundary).
//   * Session persistence is delegated to the canonical Supabase
//     client (persistSession: true) — we do not manage tokens.
//
// Email backup, trainer codes, consent, drafts, onboarding, orders,
// meal work — all deferred to later phases.

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BrandButton } from '../components/BrandButton';
import { supabase, supabaseConfigured } from '../lib/supabase';
import {
  normalizeToE164,
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
} from '../lib/phone';
import {
  rpcGetCustomerProfile,
  readProfileIsComplete,
} from '../lib/customerProfile';

const OTP_LEN = 6;

// Turn a Supabase AuthError / Error into a short customer-safe string.
// We never surface stack traces, tokens, or internal service names.
function customerSafeMessage(err) {
  if (!err) return 'Something went wrong. Please try again.';
  const raw = (err.message || String(err)).toLowerCase();
  if (raw.includes('invalid') && raw.includes('phone')) {
    return 'That phone number was rejected. Please double-check it and try again.';
  }
  if (raw.includes('rate') || raw.includes('too many')) {
    return 'Too many attempts. Please wait a minute and try again.';
  }
  if (raw.includes('token') || raw.includes('otp') || raw.includes('code')) {
    return 'That code is incorrect or has expired. Please request a new code.';
  }
  if (raw.includes('network') || raw.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  return 'We could not complete that step. Please try again.';
}

// Log only sanitized technical information (never OTP, never tokens).
function logSanitized(label, err) {
  try {
    console.warn('[etff:auth]', label, {
      name: err?.name || null,
      message: err?.message || null,
      status: err?.status || null,
    });
  } catch (_) {
    /* no-op */
  }
}

export default function SignIn() {
  const nav = useNavigate();
  const location = useLocation();

  // Return-to (Phase B12): if the customer landed on /sign-in from a
  // planner route (guard redirect), auto-return them there once
  // authenticated. Accepted from either `location.state.returnTo` or
  // sessionStorage. Restricted to internal paths only — never allow
  // arbitrary external redirect targets.
  function sanitizeReturnTo(candidate) {
    if (typeof candidate !== 'string') return null;
    if (!candidate.startsWith('/')) return null;
    if (candidate.startsWith('//')) return null;       // protocol-relative
    if (candidate.startsWith('/sign-in')) return null; // no self-loop
    return candidate;
  }
  const returnToFromState = sanitizeReturnTo(location.state?.returnTo);
  const RETURN_TO_KEY = 'etff.signin_return_to.v1';
  useEffect(() => {
    // Persist returnTo across the (potential) session refresh from
    // Supabase's onAuthStateChange, which can remount the page.
    if (returnToFromState) {
      try { sessionStorage.setItem(RETURN_TO_KEY, returnToFromState); } catch { /* ignore */ }
    }
  }, [returnToFromState]);
  function readReturnTo() {
    if (returnToFromState) return returnToFromState;
    try {
      const raw = sessionStorage.getItem(RETURN_TO_KEY);
      return sanitizeReturnTo(raw);
    } catch { return null; }
  }
  function clearReturnTo() {
    try { sessionStorage.removeItem(RETURN_TO_KEY); } catch { /* ignore */ }
  }

  const [step, setStep] = useState('phone'); // 'phone' | 'otp' | 'signed'
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneE164, setPhoneE164] = useState(''); // retained ONLY to pass to verifyOtp
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [uiError, setUiError] = useState('');
  const [info, setInfo] = useState('');
  const [session, setSession] = useState(null);

  // On mount, if a Supabase session already exists (page refresh
  // after successful verify), reflect that directly.
  useEffect(() => {
    if (!supabaseConfigured) return;
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (data?.session) {
        setSession(data.session);
        setStep('signed');
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (cancelled) return;
      setSession(s ?? null);
      if (s) setStep('signed');
    });
    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  // Auto-return to the planner (or wherever the customer came from)
  // as soon as the session is live. Before consuming returnTo we
  // check the customer profile via get_customer_profile — if it's
  // incomplete we route to /account/details first, preserving the
  // original returnTo so the customer lands on the intended page
  // once they've filled in their name + email.
  useEffect(() => {
    if (step !== 'signed' || !session) return;
    const to = readReturnTo();
    let cancelled = false;
    (async () => {
      const res = await rpcGetCustomerProfile();
      if (cancelled) return;
      const complete = res.ok ? readProfileIsComplete(res.data) : null;
      if (complete === false) {
        // Incomplete profile: force customer through Account Details.
        // Preserve returnTo (if any) via the query string so we can
        // hand them back to the correct page after saving. If `to`
        // is already an /account/details URL (customer landed on the
        // page directly while signed out), pass it through untouched
        // to avoid nesting the query string.
        clearReturnTo();
        let target;
        if (to && to.startsWith('/account/details')) {
          target = to;
        } else if (to) {
          target = `/account/details?returnTo=${encodeURIComponent(to)}`;
        } else {
          target = '/account/details';
        }
        setTimeout(() => nav(target, { replace: true }), 0);
        return;
      }
      // Complete, or profile status unknown (RPC failed) — preserve
      // the original returnTo behaviour so a transient RPC error
      // doesn't strand the customer.
      if (!to) return;
      clearReturnTo();
      setTimeout(() => nav(to, { replace: true }), 0);
    })();
    return () => { cancelled = true; };
  }, [step, session]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSendOtp(e) {
    e?.preventDefault?.();
    setUiError('');
    setInfo('');
    const v = normalizeToE164(phoneInput, countryCode);
    if (!v.ok) {
      setUiError(v.error);
      return;
    }
    if (!supabaseConfigured || !supabase) {
      setUiError('Sign-in is not available right now. Please try again later.');
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ phone: v.value });
      if (error) {
        logSanitized('signInWithOtp:error', error);
        setUiError(customerSafeMessage(error));
        return;
      }
      // Success: DO NOT log the phone number or any code.
      console.info('[etff:auth] signInWithOtp:sent');
      setPhoneE164(v.value);
      setOtp('');
      setStep('otp');
      setInfo('We sent a 6-digit code to your phone.');
    } catch (err) {
      logSanitized('signInWithOtp:threw', err);
      setUiError(customerSafeMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyOtp(e) {
    e?.preventDefault?.();
    setUiError('');
    setInfo('');
    const token = (otp || '').trim();
    if (!/^\d{4,8}$/.test(token)) {
      setUiError('Enter the 6-digit code from your text message.');
      return;
    }
    if (!phoneE164) {
      setUiError('Please request a new code.');
      setStep('phone');
      return;
    }
    if (!supabaseConfigured || !supabase) {
      setUiError('Sign-in is not available right now. Please try again later.');
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: phoneE164,
        token,
        type: 'sms',
      });
      if (error) {
        logSanitized('verifyOtp:error', error);
        setUiError(customerSafeMessage(error));
        return;
      }
      // Never log data.session (contains tokens).  Log only presence.
      console.info('[etff:auth] verifyOtp:ok', {
        hasSession: Boolean(data?.session),
        hasUser: Boolean(data?.user),
      });
      // The onAuthStateChange listener will also flip us to 'signed';
      // set it here for immediate UI response.
      setSession(data?.session ?? null);
      setStep('signed');
      setOtp('');
    } catch (err) {
      logSanitized('verifyOtp:threw', err);
      setUiError(customerSafeMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut() {
    setUiError('');
    setInfo('');
    if (!supabaseConfigured || !supabase) return;
    setBusy(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        logSanitized('signOut:error', error);
        setUiError(customerSafeMessage(error));
        return;
      }
      setSession(null);
      setPhoneE164('');
      setPhoneInput('');
      setOtp('');
      setStep('phone');
      setInfo('You have been signed out.');
    } catch (err) {
      logSanitized('signOut:threw', err);
      setUiError(customerSafeMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function handleBackToPhone() {
    setUiError('');
    setInfo('');
    setOtp('');
    setStep('phone');
  }

  return (
    <AppShell>
      <div className="wizard-card">
        <div className="eyebrow" data-testid="signin-eyebrow">
          Account
        </div>
        <h1 className="h2 mt-3" data-testid="signin-title">
          {step === 'signed' ? 'You are signed in' : 'Sign in to continue'}
        </h1>

        {!supabaseConfigured && (
          <div
            className="mt-6 p-4"
            style={{
              background: 'var(--warning-bg)',
              border: '1px solid var(--warning)',
              borderRadius: 'var(--radius)',
            }}
            data-testid="signin-not-configured"
          >
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
              Sign-in is not available right now. Please try again later.
            </p>
          </div>
        )}

        {supabaseConfigured && step === 'phone' && (
          <form onSubmit={handleSendOtp} className="mt-6" data-testid="signin-phone-form">
            <p style={{ color: 'var(--text-dim)' }}>
              Enter your mobile number and we&rsquo;ll text you a 6-digit code
              to sign in.
            </p>

            <label
              htmlFor="etff-phone"
              className="eyebrow mt-6 inline-block"
              style={{ color: 'var(--ink)' }}
            >
              Mobile number
            </label>
            <div
              className="mt-2 flex items-stretch gap-2 flex-wrap"
              style={{ maxWidth: 520 }}
            >
              <select
                aria-label="Country code"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                disabled={busy}
                style={{
                  padding: '14px 12px',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius)',
                  background: 'var(--paper)',
                  color: 'var(--ink)',
                  fontSize: '16px',
                  minWidth: 120,
                }}
                data-testid="signin-country-select"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label} ({c.code})
                  </option>
                ))}
              </select>
              <input
                id="etff-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder={
                  (COUNTRY_CODES.find((c) => c.code === countryCode) || {})
                    .example || '346 848 1414'
                }
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                disabled={busy}
                style={{
                  flex: 1,
                  minWidth: 220,
                  padding: '14px 16px',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius)',
                  background: 'var(--paper)',
                  color: 'var(--ink)',
                  fontSize: '16px',
                  letterSpacing: '0.02em',
                }}
                data-testid="signin-phone-input"
              />
            </div>
            <p
              className="mt-2 text-sm"
              style={{ color: 'var(--text-dim)' }}
              data-testid="signin-phone-hint"
            >
              We&rsquo;ll send a one-time code by text message.
            </p>

            {uiError && (
              <p
                className="mt-4 text-sm"
                style={{ color: 'var(--danger, #b03a2e)' }}
                data-testid="signin-phone-error"
              >
                {uiError}
              </p>
            )}

            <div className="mt-8 flex items-center gap-3">
              <BrandButton
                type="submit"
                variant="primary"
                disabled={busy}
                data-testid="signin-send-code-btn"
              >
                {busy ? 'Sending…' : 'Send code'}
              </BrandButton>
              <BrandButton
                type="button"
                variant="ghost"
                onClick={() => nav('/')}
                disabled={busy}
                data-testid="signin-cancel-btn"
              >
                Cancel
              </BrandButton>
            </div>
          </form>
        )}

        {supabaseConfigured && step === 'otp' && (
          <form onSubmit={handleVerifyOtp} className="mt-6" data-testid="signin-otp-form">
            <p style={{ color: 'var(--text-dim)' }} data-testid="signin-otp-sent-msg">
              {info || 'We sent a 6-digit code to your phone.'}
            </p>

            <label
              htmlFor="etff-otp"
              className="eyebrow mt-6 inline-block"
              style={{ color: 'var(--ink)' }}
            >
              6-digit code
            </label>
            <input
              id="etff-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={OTP_LEN}
              placeholder="123456"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              disabled={busy}
              className="mt-2 w-full"
              style={{
                padding: '14px 16px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius)',
                background: 'var(--paper)',
                color: 'var(--ink)',
                fontSize: '20px',
                letterSpacing: '0.4em',
                textAlign: 'center',
              }}
              data-testid="signin-otp-input"
            />

            {uiError && (
              <p
                className="mt-4 text-sm"
                style={{ color: 'var(--danger, #b03a2e)' }}
                data-testid="signin-otp-error"
              >
                {uiError}
              </p>
            )}

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <BrandButton
                type="submit"
                variant="primary"
                disabled={busy}
                data-testid="signin-verify-btn"
              >
                {busy ? 'Verifying…' : 'Verify and sign in'}
              </BrandButton>
              <BrandButton
                type="button"
                variant="outline"
                onClick={handleBackToPhone}
                disabled={busy}
                data-testid="signin-change-number-btn"
              >
                Change number
              </BrandButton>
            </div>
          </form>
        )}

        {supabaseConfigured && step === 'signed' && (
          <div className="mt-6" data-testid="signin-signed-in-panel">
            <p style={{ color: 'var(--text-dim)' }}>
              Your Supabase session is active. You can close this page or
              continue.
            </p>
            <div
              className="mt-6 p-4"
              style={{
                background: 'var(--paper-2, rgba(0,0,0,0.03))',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius)',
              }}
            >
              <div className="eyebrow" style={{ color: 'var(--ink)' }}>
                Signed-in identity
              </div>
              <p
                className="mt-2 text-sm"
                style={{ color: 'var(--ink-2)' }}
                data-testid="signin-signed-in-phone"
              >
                Phone on file:{' '}
                <strong>
                  {session?.user?.phone
                    ? '+' + session.user.phone
                    : phoneE164 || 'unknown'}
                </strong>
              </p>
              <p
                className="mt-1 text-sm"
                style={{ color: 'var(--ink-2)' }}
                data-testid="signin-signed-in-uid"
              >
                Supabase user id:{' '}
                <strong>{session?.user?.id || '—'}</strong>
              </p>
            </div>

            {uiError && (
              <p
                className="mt-4 text-sm"
                style={{ color: 'var(--danger, #b03a2e)' }}
                data-testid="signin-signout-error"
              >
                {uiError}
              </p>
            )}

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <BrandButton
                type="button"
                variant="outline"
                onClick={handleSignOut}
                disabled={busy}
                data-testid="signin-signout-btn"
              >
                {busy ? 'Signing out…' : 'Sign out'}
              </BrandButton>
              <BrandButton
                type="button"
                variant="ghost"
                onClick={() => nav('/')}
                disabled={busy}
                data-testid="signin-home-btn"
              >
                Back to home
              </BrandButton>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
